import { useState } from 'react';
import type { RoomView } from '@bm/shared';
import { de } from '@/i18n/de';
import { useAppStore } from '@/lib/store';
import { wsClient } from '@/lib/wsClient';
import { mafiaColleagueNames } from '@/lib/colleagues';
import { PhaseChip } from '@/components/PhaseChip';
import { HistorySheet } from '@/components/HistorySheet';
import { RoleCard } from '@/components/RoleCard';
import { RulesSheet } from '@/components/RulesSheet';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { Sheet } from '@/components/Sheet';

type OpenSheet = 'menu' | 'role' | 'history' | 'rules' | 'leave' | 'close' | null;

/**
 * Ton und Historie lagen früher als zwei Icons im Header, das Verlassen gab es
 * nur über den Android-Zurück-Button — am Desktop also gar nicht. Beides sitzt
 * jetzt hinter einem Menü: ein Weg für alle Nebenaktionen, und der Header
 * bleibt neben dem Phasen-Chip schmal.
 */
export function GameHeader({ view }: { view: RoomView }) {
  const [open, setOpen] = useState<OpenSheet>(null);
  const soundEnabled = useAppStore((s) => s.soundEnabled);
  const setSoundEnabled = useAppStore((s) => s.setSoundEnabled);

  return (
    <>
      <PhaseChip phase={view.phase} round={view.roundNumber} />
      <button
        type="button"
        onClick={() => setOpen('menu')}
        aria-label={de.common.menu}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg text-fg-muted hover:bg-surface-2"
      >
        ⋯
      </button>

      <Sheet open={open === 'menu'} onClose={() => setOpen(null)} title={de.common.menu}>
        <ul className="flex flex-col gap-2">
          {/* Zuerst, weil es die häufigste Frage einer laufenden Runde ist:
              „war ich nochmal Bürger?". In der Lobby gibt es noch keine Rolle. */}
          {view.self.role !== null && (
            <li>
              <MenuItem icon="🂠" label={de.common.myRole} onClick={() => setOpen('role')} />
            </li>
          )}
          <li>
            <MenuItem icon="📜" label={de.history.title} onClick={() => setOpen('history')} />
          </li>
          <li>
            <MenuItem icon="❓" label={de.rules.open} onClick={() => setOpen('rules')} />
          </li>
          <li>
            <MenuItem
              icon={soundEnabled ? '🔊' : '🔇'}
              label={soundEnabled ? de.common.soundOff : de.common.soundOn}
              onClick={() => setSoundEnabled(!soundEnabled)}
            />
          </li>
          <li>
            <MenuItem icon="🚪" label={de.lobby.leave} onClick={() => setOpen('leave')} />
          </li>
          {/* Nur der Host: Auflösen beendet die Runde für alle, nicht nur für
              den, der tippt. */}
          {view.self.isHost && (
            <li>
              <MenuItem icon="🧹" label={de.common.closeRoom} onClick={() => setOpen('close')} />
            </li>
          )}
        </ul>
      </Sheet>

      {/* Dieselbe Karte wie beim ersten Aufdecken — also derselbe Schutz vor
          Mitlesen: die Rolle steht nur da, solange der Finger sie hält. Das
          Sheet wird beim Schließen abgebaut, die Karte startet dadurch bei
          jedem Öffnen wieder verdeckt. */}
      <Sheet open={open === 'role'} onClose={() => setOpen(null)} title={de.common.myRole}>
        <div className="flex flex-col items-center gap-3 pb-2">
          <RoleCard role={view.self.role} colleagueNames={mafiaColleagueNames(view)} />
          <p className="text-center text-sm text-fg-muted">{de.common.myRoleHint}</p>
        </div>
      </Sheet>

      <HistorySheet view={view} open={open === 'history'} onClose={() => setOpen(null)} />
      <RulesSheet open={open === 'rules'} onClose={() => setOpen(null)} settings={view.settings} />
      <ConfirmSheet
        open={open === 'leave'}
        title={de.common.leaveRoomConfirm}
        body={de.common.leaveRoomBody}
        confirmLabel={de.common.leaveRoomConfirmCta}
        destructive
        onConfirm={() => {
          setOpen(null);
          wsClient.send({ type: 'room:leave' });
        }}
        onCancel={() => setOpen(null)}
      />
      <ConfirmSheet
        open={open === 'close'}
        title={de.common.closeRoomConfirm}
        body={de.common.closeRoomBody}
        confirmLabel={de.common.closeRoomConfirmCta}
        destructive
        onConfirm={() => {
          setOpen(null);
          wsClient.send({ type: 'room:close' });
        }}
        onCancel={() => setOpen(null)}
      />
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-12 w-full items-center gap-3 rounded-xl border border-surface-border bg-bg px-4 py-3 text-left font-medium hover:border-accent/60"
    >
      <span aria-hidden="true">{icon}</span>
      {label}
    </button>
  );
}
